import { useEffect, useState } from "react";
import { Card } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { BellRing, Send, AlertCircle } from "lucide-react";
import { toast } from "sonner";
import {
  pushSupported, pushPermissionState, currentSubscription,
  enablePush, disablePush, sendTestPush,
} from "@/lib/pushClient";

/**
 * PushNotificationsCard — user-facing toggle to enable/disable Web Push.
 * Renders on the resident profile page but works for any authenticated role.
 */
export function PushNotificationsCard() {
  const [supported, setSupported] = useState(false);
  const [permission, setPermission] = useState("default");
  const [subscribed, setSubscribed] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    (async () => {
      setSupported(pushSupported());
      setPermission(await pushPermissionState());
      const sub = await currentSubscription();
      setSubscribed(!!sub);
    })();
  }, []);

  const toggle = async (next) => {
    setBusy(true);
    try {
      if (next) {
        await enablePush();
        toast.success("Push notifications enabled");
        setSubscribed(true);
        setPermission("granted");
      } else {
        await disablePush();
        toast.success("Push notifications disabled");
        setSubscribed(false);
      }
    } catch (e) {
      toast.error(e?.message || "Couldn't toggle push");
    }
    setBusy(false);
  };

  const test = async () => {
    setBusy(true);
    try {
      const { data } = await sendTestPush();
      if (data.sent > 0) toast.success(`Test push fired · ${data.sent} device(s)`);
      else toast.warning("No active subscription found — flip the switch on first.");
    } catch (e) {
      toast.error(e?.message || "Test failed");
    }
    setBusy(false);
  };

  return (
    <Card className="p-6 mt-4 bg-white" data-testid="push-notifications-card">
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0">
          <div className="text-xs uppercase tracking-widest text-zinc-500 flex items-center gap-2">
            <BellRing className="h-3.5 w-3.5" /> Browser push notifications
          </div>
          <div className="text-lg font-semibold font-display mt-1 flex items-center gap-2">
            {subscribed ? "You're subscribed" : "Push is off"}
            {subscribed && <Badge variant="outline" className="border-emerald-500/40 text-emerald-700 bg-emerald-500/10">Live</Badge>}
          </div>
          <div className="text-sm text-zinc-600 mt-1">
            Get real-time alerts for board digests, payment reminders, RFP updates, and safety notices — even when the app is closed.
          </div>
          {!supported && (
            <div className="mt-3 rounded-md bg-amber-50 border border-amber-200 text-amber-900 text-xs p-3 flex items-start gap-2" data-testid="push-unsupported">
              <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" />
              <div>This browser doesn&apos;t support Web Push. Try Chrome, Firefox, Edge, or Safari 16+.</div>
            </div>
          )}
          {supported && permission === "denied" && (
            <div className="mt-3 rounded-md bg-rose-50 border border-rose-200 text-rose-900 text-xs p-3 flex items-start gap-2" data-testid="push-denied">
              <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" />
              <div>You blocked notifications for this site. Update site settings in your browser to re-enable.</div>
            </div>
          )}
        </div>
        <div className="shrink-0 flex flex-col items-end gap-2">
          <Switch
            data-testid="push-toggle"
            checked={subscribed}
            disabled={busy || !supported || permission === "denied"}
            onCheckedChange={toggle}
          />
          <div className="text-[10px] uppercase tracking-widest text-zinc-500">
            {subscribed ? "on" : "off"}
          </div>
          {subscribed && (
            <Button
              onClick={test}
              disabled={busy}
              size="sm"
              variant="outline"
              className="rounded-full mt-1"
              data-testid="push-test-btn"
            >
              <Send className="h-3 w-3 mr-1" /> Send test
            </Button>
          )}
        </div>
      </div>
    </Card>
  );
}

export default PushNotificationsCard;
